import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { LegalDocumentBody } from '@/components/legal/legal-document.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { LEGAL_MENU } from '@/data/site.ts';
import type { LegalDocument, LegalDocumentId } from '@/data/legal.ts';
import type { PageProps } from '@/types/props.ts';

/** 시안 `popup-back` 의 인라인 SVG */
const BackIcon: FunctionalComponent = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M11.67 3.86961L9.9 2.09961L0 11.9996L9.9 21.8996L11.67 20.1296L3.54 11.9996L11.67 3.86961Z"
      fill="currentColor"
    />
  </svg>
);

function isLegalId(value: string | undefined): value is LegalDocumentId {
  return value === 'service' || value === 'privacy' || value === 'consent';
}

/**
 * 목록(1단계)을 거쳐 들어왔는지.
 *
 * 시안에서는 목록과 본문이 **한 팝업의 두 상태**라 히스토리가 하나다. 그래서
 * 목록 ↔ 본문 이동은 `replace` 로 처리하고, 거쳐 왔는지는 이 플래그로 기억한다.
 * 푸터 브랜드나 웹진 푸터처럼 본문으로 바로 들어온 경우에는 돌아갈 목록이 없으므로
 * 뒤로가기 버튼을 아예 보여주지 않는다.
 */
let enteredFromMenu = false;

/** 팝업을 닫으면 팝업을 열기 직전 화면으로. 직접 들어왔으면 홈으로. */
function close() {
  enteredFromMenu = false;
  if (window.history.length > 1) window.history.back();
  else route('/', true);
}

interface Props {
  /** 라우트가 고정으로 넘긴다. 없으면 메뉴(1단계). */
  doc?: LegalDocumentId;
}

/**
 * 약관·방침 화면 (시안 `_service.html` · `_privacy.html` · `_consent.html`).
 *
 * 시안은 팝업 하나에 두 단계를 담는다 — 1단계 목록(`.popup-menu`), 2단계 본문
 * (`.popup-detail`), 뒤로가기로 목록 복귀. 여기서는 단계마다 주소를 나눴다.
 * 약관은 링크로 공유되는 문서라 각자 주소를 갖는 편이 낫고, 뒤로가기 동작도
 * 브라우저 히스토리와 어긋나지 않는다.
 *
 *   `/terms`    목록 (1단계)
 *   `/service`  서비스 이용약관
 *   `/privacy`  개인정보 처리방침
 *   `/consent`  개인정보 수집·이용 동의
 *
 * 시안 파일들은 팝업만 `display:block` 으로 열어 둔 형태다. 즉 시안 자체가 이
 * 화면을 단독 페이지처럼 보여준다.
 */
const Legal: FunctionalComponent<PageProps<Props>> = ({ doc }) => {
  const valid = isLegalId(doc);
  const [legalDoc, setLegalDoc] = useState<LegalDocument | null>(null);
  /** 마운트 시점에 고정한다. 이후 플래그가 바뀌어도 이 화면의 헤더는 흔들리지 않는다. */
  const [fromMenu] = useState(() => enteredFromMenu);

  useEffect(() => {
    if (doc && !valid) {
      route('/terms', true);
      return;
    }
    if (!valid) {
      setLegalDoc(null);
      return;
    }

    // 본문이 7만 자가 넘는다. 첫 화면 번들에 싣지 않고 이 화면에서만 불러온다.
    let cancelled = false;
    void import('@/data/legal.ts').then((module) => {
      if (!cancelled) setLegalDoc(module.LEGAL_DOCUMENTS[doc]);
    });

    return () => {
      cancelled = true;
    };
  }, [doc, valid]);

  if (doc && !valid) return null;

  return (
    <div class="page">
      <div class="common-popup-overlay is-open">
        <div class="common-popup">
          {/* 목록에서 들어온 본문에만 뒤로가기를 둔다. 그 외에는 닫기만. */}
          <div class={valid && !fromMenu ? 'popup-header' : 'popup-header popup-header2'}>
            {valid ? (
              fromMenu && (
                <button
                  type="button"
                  class="popup-back is-open"
                  aria-label="목록으로"
                  onClick={() => {
                    enteredFromMenu = false;
                    route('/terms', true);
                  }}
                >
                  <BackIcon />
                </button>
              )
            ) : (
              <span class="popup-title">약관 및 정책</span>
            )}
            <button type="button" class="popup-close" aria-label="닫기" onClick={close}>
              <img src="/images/close.png" alt="" />
            </button>
          </div>

          <div class="popup-body">
            {valid ? (
              <div class="popup-detail is-open">
                {legalDoc ? <LegalDocumentBody doc={legalDoc} /> : <LoadingSpinner show />}
              </div>
            ) : (
              <ul class="popup-menu is-open">
                {LEGAL_MENU.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`/${item.id}`}
                      class="popup-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        // 시안처럼 한 팝업의 상태 전환으로 다룬다. 히스토리를 늘리지 않아
                        // 본문에서 닫으면 팝업을 열기 직전 화면으로 곧장 돌아간다.
                        enteredFromMenu = true;
                        route(`/${item.id}`, true);
                      }}
                    >
                      {item.label}
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Legal;
